import { HyperliquidInfoClient } from "@hyperhedge/hyperliquid/client";
import { useQuery } from "@tanstack/react-query";
import type { PriceChartDatum } from "../components/price-chart/price-chart";

const client = new HyperliquidInfoClient();

export interface UsePerpChartOptions {
  /** Candle interval. Default: "1m". */
  interval?: string;
  /** Lookback window in ms. Default: 2h. */
  lookbackMs?: number;
  /** Mid-price poll interval in ms. Default: 3000. */
  midPollMs?: number;
}

export interface UsePerpChartResult {
  data: PriceChartDatum[];
  currentMid: number | undefined;
  isReady: boolean;
  error: Error | null;
}

/**
 * Slim REST-only price feed for Hyperliquid perps.
 *
 * - Fetches historical candles via `/info` (cached 30s).
 * - Polls `allMids` every few seconds for live mark.
 * - Appends the latest mid as a synthetic "now" datum so the line stays flush
 *   with the right edge without waiting for the next candle close.
 */
export function usePerpChart(
  coin: string | null,
  opts: UsePerpChartOptions = {},
): UsePerpChartResult {
  const interval = opts.interval ?? "1m";
  const lookbackMs = opts.lookbackMs ?? 2 * 60 * 60 * 1000;
  const midPollMs = opts.midPollMs ?? 3000;

  const candlesQ = useQuery({
    queryKey: ["hl", "candles", coin, interval, lookbackMs],
    queryFn: async () => {
      if (!coin) return [];
      const now = Date.now();
      return client.getCandleSnapshot(coin, interval, now - lookbackMs, now);
    },
    enabled: !!coin,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const midsQ = useQuery({
    queryKey: ["hl", "allMids"],
    queryFn: () => client.getAllMids(),
    enabled: !!coin,
    staleTime: midPollMs,
    refetchInterval: midPollMs,
  });

  const candles = candlesQ.data ?? [];
  const currentMid = coin
    ? (() => {
        const raw = midsQ.data?.[coin];
        const n = raw !== undefined ? Number(raw) : NaN;
        return Number.isFinite(n) ? n : undefined;
      })()
    : undefined;

  const data: PriceChartDatum[] = candles.map((c) => ({
    time: c.t,
    value: Number(c.c),
  }));

  // Append a synthetic "now" point at the live mid so the live dot sits
  // at the right edge instead of lagging behind the latest closed candle.
  if (data.length > 0 && currentMid != null) {
    const last = data[data.length - 1]!;
    const now = Date.now();
    if (now > last.time + 1000) {
      data.push({ time: now, value: currentMid });
    }
  }

  return {
    data,
    currentMid,
    isReady: candlesQ.isSuccess,
    error: (candlesQ.error ?? midsQ.error ?? null) as Error | null,
  };
}
